/**
 * A administração dos campos personalizados (FR-79, 11.3) — **uma** forma para os dois donos.
 *
 * A definição mora no serviço dono da entidade que carrega o campo: `cadastro` para Empresa,
 * `negocios` para Negócio (AD-3, Convenções da espinha). As rotas e os corpos são os mesmos nos
 * dois (`compartilhado/campos_personalizados.py` no backend), e por isso as funções e os hooks
 * nascem de uma fábrica parametrizada pelo serviço, e não de duas cópias que divergiriam.
 *
 *     GET    /campos-personalizados              as ativas
 *     POST   /campos-personalizados              { nome, tipo, opcoes }   -> 201 (200 se reativou)
 *     PUT    /campos-personalizados/{campo_id}   { nome, ordem, opcoes }  -> 200 (nunca o tipo)
 *     DELETE /campos-personalizados/{campo_id}   exclusão lógica          -> 200
 *
 * **Cada dono é tipado pelo contrato gerado dele** (AD-16): `camposDaEmpresa` pelos tipos do
 * `cadastro`, `camposDoNegocio` pelos do `negocios` — um serviço nunca empresta o contrato do outro.
 * O cartão compartilhado recebe a união dos dois; a forma coincide porque os dois servidores usam a
 * mesma declaração (`compartilhado/campos_personalizados.py`), e é o `tsc` que o confere.
 * Nome com radical de custo/margem é `422` com a mensagem do serviço — a tela a mostra como veio.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { chamar, chamarComStatus } from '@/api/cliente'
import type { components as ComponentesDoCadastro } from '@/api/tipos-gerados/cadastro'
import type { components as ComponentesDoNegocios } from '@/api/tipos-gerados/negocios'

/** Os serviços que carregam campo personalizado. */
export type DonoDeCampo = 'cadastro' | 'negocios'

/** Os esquemas gerados **de cada dono** — o `negocios` nunca é tipado pelo `cadastro`. */
interface EsquemasPorDono {
  cadastro: ComponentesDoCadastro['schemas']
  negocios: ComponentesDoNegocios['schemas']
}

/** Uma definição de campo personalizado, no contrato do dono `D`. */
export type CampoDoDono<D extends DonoDeCampo> = EsquemasPorDono[D]['RespostaDoCampo']

/** Uma definição de campo personalizado, de qualquer dono (o que o cartão desenha). */
export type CampoPersonalizado = CampoDoDono<DonoDeCampo>

/** Os quatro tipos do FR-79. */
export type TipoDeCampo = EsquemasPorDono[DonoDeCampo]['TipoDeCampo']

/** O corpo de criar uma definição, no contrato do dono `D`. */
export type NovoCampo<D extends DonoDeCampo = DonoDeCampo> = EsquemasPorDono[D]['CorpoDeNovoCampo']

/** O corpo de editar uma definição — sem `tipo`, que não se edita. */
export type EdicaoDeCampo<D extends DonoDeCampo = DonoDeCampo> =
  EsquemasPorDono[D]['CorpoDeEdicaoDeCampo']

/**
 * O desfecho de criar: o campo e se ele foi **reativado** — o nome existia excluído e o serviço o
 * trouxe de volta (`200`, mesmo `id`), em vez de criar outro (`201`).
 */
export interface CampoCriado<D extends DonoDeCampo = DonoDeCampo> {
  campo: CampoDoDono<D>
  restaurado: boolean
}

/** O nome de cada tipo na tela. `Record<TipoDeCampo, …>` é o guarda de exaustividade. */
export const NOMES_DOS_TIPOS_DE_CAMPO: Record<TipoDeCampo, string> = {
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  lista: 'Lista',
}

function caminhoDoCampo(campoId: string): string {
  return `/campos-personalizados/${encodeURIComponent(campoId)}`
}

/** As funções e os hooks da administração dos campos de um dono. */
export function criarApiDeCamposPersonalizados<D extends DonoDeCampo>(dono: D) {
  type Campo = CampoDoDono<D>
  const chave = [dono, 'campos-personalizados'] as const

  function lerCampos(): Promise<Campo[]> {
    return chamar<Campo[]>(dono, '/campos-personalizados')
  }

  /** `201` criou; `200` **reativou** o campo excluído com aquele nome (mesmo `id`). */
  async function criarCampo(novo: NovoCampo<D>): Promise<CampoCriado<D>> {
    const { corpo, status } = await chamarComStatus<Campo>(dono, '/campos-personalizados', {
      method: 'POST',
      corpo: { nome: novo.nome, tipo: novo.tipo, opcoes: novo.opcoes },
    })
    return { campo: corpo, restaurado: status === 200 }
  }

  function editarCampo(campoId: string, edicao: EdicaoDeCampo<D>): Promise<Campo> {
    return chamar<Campo>(dono, caminhoDoCampo(campoId), {
      method: 'PUT',
      corpo: { nome: edicao.nome, ordem: edicao.ordem, opcoes: edicao.opcoes },
    })
  }

  function excluirCampo(campoId: string): Promise<Campo> {
    return chamar<Campo>(dono, caminhoDoCampo(campoId), { method: 'DELETE' })
  }

  function useCampos() {
    return useQuery({ queryKey: chave, queryFn: lerCampos, retry: false })
  }

  function useInvalidar() {
    const clienteDeConsultas = useQueryClient()
    return () => clienteDeConsultas.invalidateQueries({ queryKey: chave })
  }

  function useCriarCampo() {
    const aoTerminar = useInvalidar()
    return useMutation({
      mutationFn: (novo: NovoCampo<D>) => criarCampo(novo),
      onSuccess: aoTerminar,
    })
  }

  function useEditarCampo() {
    const aoTerminar = useInvalidar()
    return useMutation({
      mutationFn: ({ campoId, edicao }: { campoId: string; edicao: EdicaoDeCampo<D> }) =>
        editarCampo(campoId, edicao),
      onSuccess: aoTerminar,
    })
  }

  function useExcluirCampo() {
    const aoTerminar = useInvalidar()
    return useMutation({
      mutationFn: (campoId: string) => excluirCampo(campoId),
      onSuccess: aoTerminar,
    })
  }

  return {
    dono,
    chave,
    lerCampos,
    criarCampo,
    editarCampo,
    excluirCampo,
    useCampos,
    useCriarCampo,
    useEditarCampo,
    useExcluirCampo,
  }
}

/** O que a fábrica devolve para o dono `D`. */
export type ApiDoDono<D extends DonoDeCampo> = ReturnType<typeof criarApiDeCamposPersonalizados<D>>

/** O que o cartão recebe por prop: a API de **um** dos dois donos. */
export type ApiDeCamposPersonalizados = ApiDoDono<'cadastro'> | ApiDoDono<'negocios'>
